
import React, { useState, useEffect } from "react";
import { useGeminiApi, GeminiRequestOptions } from "@/hooks/useGeminiApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Send, AlertTriangle, InfoIcon, Wrench } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useSubscription } from "@/hooks/useSubscription";
import { Progress } from "@/components/ui/progress"; 

const GeminiPlayground: React.FC = () => {
  const { generateContent, testDirectApi, isLoading, error, quotaInfo } = useGeminiApi();
  const [prompt, setPrompt] = useState("");
  const [systemInstruction, setSystemInstruction] = useState("");
  const [temperature, setTemperature] = useState(0.7);
  const [result, setResult] = useState<string | null>(null);
  const [responseType, setResponseType] = useState<"text" | "json">("text");
  const [model, setModel] = useState("gemini-2.0-flash");
  const [debugInfo, setDebugInfo] = useState<{ success: boolean, info: string } | null>(null);
  const { subscriptionData, isLoading: isLoadingSubscription } = useSubscription();
  const [isTesting, setIsTesting] = useState(false);

  const handleSubmit = async () => {
    if (!prompt.trim()) return;

    const options: GeminiRequestOptions = {
      model,
      temperature,
      responseType,
      systemInstruction: systemInstruction.trim() || undefined
    };

    setDebugInfo(null);
    setResult(null);
    console.log("Submitting with options:", options);
    
    const response = await generateContent(prompt, options);
    if (response) {
      setResult(response.generatedText);
      setDebugInfo({
        success: true,
        info: `Successfully generated response using model: ${options.model}`
      });
    } else {
      setDebugInfo({
        success: false,
        info: `Error occurred. Check console for details.`
      });
    }
  };

  const handleTestDirectApi = async () => {
    setIsTesting(true);
    setDebugInfo(null);
    setResult(null);
    
    await testDirectApi();
    
    setIsTesting(false);
    setDebugInfo({
      success: !error,
      info: error ? `API test failed: ${error}` : `API test succeeded! Check console for details.`
    });
  };

  return (
    <div className="container mx-auto py-6 max-w-6xl">
      <div className="flex flex-col space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold">Gemini AI Playground</h1>
          </div>
        </div>
        
        {subscriptionData && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Subscription Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Analysis Tokens: {subscriptionData.analysisUsage.used.toLocaleString()} / {subscriptionData.analysisUsage.limit.toLocaleString()}</span>
                  <span className="text-muted-foreground">{subscriptionData.analysisUsage.percentage.toFixed(1)}%</span>
                </div>
                <Progress value={subscriptionData.analysisUsage.percentage} className="h-2" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Diagnostic Tools Card */}
        <Card className="border-amber-500 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Diagnostic Tools
            </CardTitle>
            <CardDescription>
              Tools to help diagnose API connectivity issues
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              onClick={handleTestDirectApi} 
              disabled={isTesting}
              className="w-full sm:w-auto"
            >
              {isTesting ? "Testing API..." : "Test Direct API Connection"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              This will test a direct connection to the Gemini API using the curl command pattern to help diagnose API key issues.
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Configuration</CardTitle>
                <CardDescription>Customize how Gemini responds to your prompts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="model">Model</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash</SelectItem>
                      <SelectItem value="gemini-2.0-flash-001">Gemini 2.0 Flash (Stable)</SelectItem>
                      <SelectItem value="gemini-2.0-flash-exp">Gemini 2.0 Flash (Experimental)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label htmlFor="temperature">Temperature: {temperature}</Label>
                  </div>
                  <Slider 
                    id="temperature"
                    min={0} 
                    max={2} 
                    step={0.1} 
                    value={[temperature]}
                    onValueChange={(value) => setTemperature(value[0])}
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower values produce more focused and deterministic outputs. Higher values produce more diverse and creative outputs.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="responseType">Response Format</Label>
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="json-toggle" 
                      checked={responseType === "json"}
                      onCheckedChange={(checked) => setResponseType(checked ? "json" : "text")}
                    />
                    <Label htmlFor="json-toggle">JSON output</Label>
                  </div>
                  {responseType === "json" && (
                    <p className="text-xs text-muted-foreground">
                      Returns a structured JSON response rather than plain text.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Instructions</CardTitle>
                <CardDescription>Set behavior guidelines for the AI</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="e.g., You are a helpful assistant that speaks like Shakespeare."
                  className="min-h-[100px]"
                  value={systemInstruction}
                  onChange={(e) => setSystemInstruction(e.target.value)}
                />
              </CardContent>
            </Card>
          </div>

          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Prompt</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter your prompt here..."
                  className="min-h-[150px]"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </CardContent>
              <CardFooter className="flex justify-between">
                <div className="text-sm text-muted-foreground">
                  {prompt ? `${prompt.length} characters` : ""}
                </div>
                <Button onClick={handleSubmit} disabled={isLoading || !prompt.trim()}>
                  {isLoading ? "Generating..." : "Generate"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Response</CardTitle>
                {error && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                {quotaInfo && (
                  <Alert className="mt-2 border-amber-500">
                    <InfoIcon className="h-4 w-4" />
                    <AlertTitle>Quota Limit Reached</AlertTitle>
                    <AlertDescription className="text-sm">
                      You've used {quotaInfo.currentUsage?.toLocaleString() || '0'} of your {quotaInfo.limit?.toLocaleString() || '0'} token monthly quota.
                      {quotaInfo.resetDate && <> Your quota will reset on {new Date(quotaInfo.resetDate).toLocaleDateString()}.</>}
                    </AlertDescription>
                  </Alert>
                )}
                {debugInfo && (
                  <Alert variant={debugInfo.success ? "default" : "destructive"} className="mt-2">
                    <AlertDescription className="text-xs">{debugInfo.info}</AlertDescription>
                  </Alert>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                  {isLoading || isTesting ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : result ? (
                    <pre className="whitespace-pre-wrap font-sans">{result}</pre>
                  ) : (
                    <div className="text-muted-foreground text-center py-8">
                      Generate content to see the results here
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeminiPlayground;
